import { Controller } from "@hotwired/stimulus"

// Per Experience item: debounced AI content analysis (premium) + AI generate bullets.
// Persists results by writing analysis JSON into the hidden
// [data-content-analyzer-target="store"] field so it rides the editor's existing
// single-writer autosave. Caches by a content fingerprint to avoid redundant calls.
export default class extends Controller {
  static targets = ["store", "panel", "notice"]
  static values = { analyzeUrl: String, generateUrl: String, paid: Boolean }

  connect() {
    this.timer = null
    this.fingerprint = this._storedFingerprint()
    this._onInput = this._onInput.bind(this)
    this.element.addEventListener("input", this._onInput)
  }

  disconnect() {
    this.element.removeEventListener("input", this._onInput)
    if (this.timer) clearTimeout(this.timer)
  }

  _onInput() { this.scheduleAnalyze() }

  scheduleAnalyze() {
    if (!this.paidValue) return
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this._analyze(false), 1500)
  }

  reanalyze() { this._analyze(true) }

  async _analyze(force) {
    if (!this.paidValue) return
    const payload = this._payload()
    if (!this._bullets(payload.details).length) return // nothing to analyze yet
    const fp = this._fingerprint(payload)
    if (!force && fp === this.fingerprint) return       // cache hit

    try {
      const res = await fetch(this.analyzeUrlValue, this._post(payload))
      if (res.status === 403) return this._notify("Premium required for AI analysis.")
      const data = await res.json()
      if (!data || !data.ok) return this._notify((data && data.error) || "AI unavailable.")
      this.fingerprint = fp
      if (this.hasStoreTarget) this.storeTarget.value = JSON.stringify({ ...data.analysis, fingerprint: fp })
      this._render(data.analysis)
      this._notify("")
      this._save()
      document.dispatchEvent(new CustomEvent("content-analyzer:scored"))
    } catch (e) {
      this._notify("AI unavailable.")
    }
  }

  async generate() {
    if (!this.paidValue) return
    try {
      const res = await fetch(this.generateUrlValue, this._post(this._payload()))
      if (res.status === 403) return this._notify("Premium required.")
      const data = await res.json()
      if (!data || !data.ok) return this._notify((data && data.error) || "Could not generate bullets.")
      this._insertBullets(data.bullets || [])
      this._notify("")
      this.scheduleAnalyze()
    } catch (e) {
      this._notify("AI unavailable.")
    }
  }

  applyFix(event) {
    const fix = (event.currentTarget.dataset.fix || "").trim()
    const nums = (event.currentTarget.dataset.bullets || "")
      .split(",").map((n) => parseInt(n, 10)).filter((n) => n > 0)
    if (!fix || !nums.length) return // nothing to target
    const ed = this._details()
    if (!ed) return
    const bullets = this._parseBullets(ed.innerHTML)
    const idx = nums[0] - 1 // replace the first referenced bullet
    if (idx < 0 || idx >= bullets.length) return
    bullets[idx] = fix
    this._setBullets(ed, bullets) // rewrite as a clean <ul><li> list so the fix sticks
    this._fireEdit(ed)
    this.scheduleAnalyze()
  }

  // ---- DOM helpers ----
  _details() { return this.element.querySelector('[data-resume-item-field="details"]') }

  _field(name) {
    const el = this.element.querySelector(`[data-resume-item-field="${name}"]`)
    if (!el) return ""
    return el.isContentEditable ? el.innerHTML : el.value
  }

  _payload() {
    return {
      role: this._field("role"),
      company: this._field("company"),
      dates: { start: this._field("start"), end: this._field("end") },
      details: this._field("details")
    }
  }

  _plain(html) {
    const d = document.createElement("div")
    d.innerHTML = html || ""
    return (d.textContent || "").replace(/\s+/g, " ").trim()
  }

  _bullets(html) { return this._parseBullets(html) }

  // Split details into logical bullets the SAME way the server's extract_bullets
  // does, so the AI's 1-based bullet numbers line up with what we edit here:
  // normalize li/p/div/br boundaries to newlines; if leading bullet glyphs are
  // present, fold non-marker lines into the previous bullet (wrapped
  // continuations); otherwise each line is its own bullet.
  _parseBullets(html) {
    const normalized = String(html || "")
      .replace(/<\s*br\s*\/?>/gi, "\n")
      .replace(/<\s*\/\s*(?:li|p|div)\s*>/gi, "\n")
    const d = document.createElement("div")
    d.innerHTML = normalized
    const lines = (d.textContent || "")
      .split(/\r?\n/).map((s) => s.replace(/\s+/g, " ").trim()).filter(Boolean)
    const marker = /^[•‣◦⁃–—\-*]+\s*/
    if (!lines.some((l) => marker.test(l))) return lines
    const bullets = []
    lines.forEach((l) => {
      if (marker.test(l)) bullets.push(l.replace(marker, ""))
      else if (bullets.length) bullets[bullets.length - 1] = (bullets[bullets.length - 1] + " " + l).trim()
      else bullets.push(l)
    })
    return bullets.filter(Boolean)
  }

  // Rewrite the details editor as a clean, escaped <ul><li> list.
  _setBullets(ed, bullets) {
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const items = bullets.map((b) => String(b).trim()).filter(Boolean).map((b) => `<li>${esc(b)}</li>`).join("")
    ed.innerHTML = items ? `<ul>${items}</ul>` : ""
  }

  _fingerprint(p) {
    const s = [p.role, p.company, p.dates.start, p.dates.end, this._bullets(p.details).join("|")].join("")
    let h = 5381
    for (let i = 0; i < s.length; i++) h = (((h << 5) + h) + s.charCodeAt(i)) >>> 0
    return String(h)
  }

  _storedFingerprint() {
    if (!this.hasStoreTarget) return null
    try { return JSON.parse(this.storeTarget.value || "{}").fingerprint || null } catch (e) { return null }
  }

  _insertBullets(bullets) {
    const ed = this._details()
    if (!ed) return
    // Append the generated bullets to the existing ones, normalized to a clean list.
    const merged = this._parseBullets(ed.innerHTML).concat(bullets.map((b) => String(b).trim()).filter(Boolean))
    this._setBullets(ed, merged)
    this._fireEdit(ed)
  }

  // Dispatch a bubbling input event so the editor's autosave (bound to the
  // editable's data-action="input->resume-editor#scheduleSave") fires.
  _fireEdit(ed) { ed.dispatchEvent(new Event("input", { bubbles: true })) }

  _post(payload) {
    return {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify(payload)
    }
  }

  // Persist a store-only change (no editable edit) by asking the root
  // resume-editor controller to schedule a save.
  _save() {
    const root = this.element.closest('[data-controller~="resume-editor"]')
    if (!root) return
    const ctrl = this.application.getControllerForElementAndIdentifier(root, "resume-editor")
    if (ctrl && typeof ctrl.scheduleSave === "function") ctrl.scheduleSave()
  }

  _notify(msg) {
    if (!this.hasNoticeTarget) return
    this.noticeTarget.textContent = msg || ""
    this.noticeTarget.classList.toggle("hidden", !msg)
  }

  // Re-render the analysis panel (mirrors _content_analysis.html.erb; escapes all text).
  _render(a) {
    if (!this.hasPanelTarget) return
    const issues = Array.isArray(a.issues) ? a.issues : []
    const passed = Array.isArray(a.passed) ? a.passed : []
    const score = parseInt(a.score, 10) || 0
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))

    if (!issues.length && !passed.length) {
      this.panelTarget.innerHTML = '<p class="text-xs text-on-surface-variant">Edit a bullet to run AI content analysis.</p>'
      return
    }

    const issueRows = issues.map((iss) => {
      const isErr = iss.severity === "error"
      const hasTarget = iss.fix && Array.isArray(iss.bullets) && iss.bullets.length > 0
      const fixBtn = hasTarget
        ? `<button type="button" data-action="content-analyzer#applyFix" data-fix="${esc(iss.fix)}" data-bullets="${esc((iss.bullets || []).join(","))}" class="mt-0.5 text-[11px] font-bold text-on-surface underline hover:text-primary-fixed">Apply fix</button>`
        : ""
      return `<li class="flex items-start gap-2">
        <span class="material-symbols-outlined text-[16px] ${isErr ? "text-red-500" : "text-amber-500"}">${isErr ? "cancel" : "error"}</span>
        <div class="min-w-0">
          <p class="text-xs font-semibold text-on-surface">${esc(iss.label)}</p>
          <p class="text-xs text-on-surface-variant">${esc(iss.message)}</p>
          ${fixBtn}
        </div></li>`
    }).join("")

    const passedBlock = passed.length
      ? `<details class="mt-2"><summary class="text-xs font-semibold text-teal-600 cursor-pointer">${passed.length} best practices applied</summary>
         <ul class="mt-1 space-y-1">${passed.map((p) => `<li class="flex items-start gap-2"><span class="material-symbols-outlined text-[16px] text-teal-500">check_circle</span><span class="text-xs text-on-surface-variant">${esc(p.label)}</span></li>`).join("")}</ul></details>`
      : ""

    this.panelTarget.innerHTML =
      `<div class="flex items-center gap-2 mb-2"><span class="text-xs font-bold text-on-surface">Content score ${score}</span>
        <span class="text-xs text-on-surface-variant">· ${issues.length} issue${issues.length === 1 ? "" : "s"} · ${passed.length} best practice${passed.length === 1 ? "" : "s"}</span></div>
       <ul class="space-y-1.5">${issueRows}</ul>${passedBlock}`
  }
}
